import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import {
  ControlContainer,
  FormGroupDirective,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { AbbIconComponent } from '@components/abb-icon/abb-icon.component';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import * as maplibregl from 'maplibre-gl';
import { environment } from '@env';
import {
  LocationClient,
  SearchForTextResult,
  SearchPlaceIndexForSuggestionsCommand,
  SearchPlaceIndexForTextCommand,
  SearchPlaceIndexForTextCommandOutput,
} from '@aws-sdk/client-location';
import { withAPIKey } from '@aws/amazon-location-utilities-auth-helper';
import {
  debounceTime,
  distinctUntilChanged,
  Subscription,
  switchMap,
} from 'rxjs';
import { CountryDetailsService } from '@services/county-details/country-details.service';

@Component({
  selector: 'abb-address-field',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    MatAutocompleteModule,
    CommonModule,
    AbbIconComponent,
    TPipe,
  ],
  template: `
    <div class="flex flex-col w-full gap-3" [ngClass]="{ 'flex-1': showMap() }">
      <mat-form-field
        class="w-full"
        color="primary"
        [ngClass]="{ 'bg-white-field': whiteField() }"
      >
        <mat-label>{{ 'Address' | t }}</mat-label>
        <input
          matInput
          id="fullAddress"
          name="fullAddress"
          formControlName="fullAddress"
          [matAutocomplete]="auto"
          required
          (blur)="onBlur()"
        />
        <abb-icon
          matSuffix
          name="mat/close"
          size="small"
          class="cursor-pointer pr-2"
          [ngClass]="{
            invisible:
              !parent.form.controls['fullAddress'].value ||
              parent.form.controls['fullAddress'].disabled
          }"
          (click)="clearSearch()"
        ></abb-icon>
        @if(parent.form.controls['fullAddress'].hasError('required')){
        <mat-error>{{ 'This field is required' | t }}</mat-error>
        } @else if(parent.form.controls['fullAddress'].invalid &&
        (parent.form.controls['fullAddress'].dirty ||
        parent.form.controls['fullAddress'].touched)){
        <mat-error>{{ 'Address is not valid' | t }}</mat-error>
        }
        <mat-autocomplete
          #auto="matAutocomplete"
          (optionSelected)="selectPlace($event.option.value)"
          class="panel"
        >
          @for (suggestion of suggestions(); track suggestion; let idx = $index)
          {
          <mat-option [id]="'address-' + idx" [value]="suggestion">
            <div class="suggestion-item">
              <abb-icon
                name="mat/location_on"
                size="small"
                class="suggestion-icon"
              ></abb-icon>
              <span>{{ suggestion.Text }}</span>
            </div>
          </mat-option>
          }
        </mat-autocomplete>
      </mat-form-field>
      @if(showMap()) {
      <div
        class="overflow-hidden flex-grow"
        id="map-search"
        #mapContainer
        style="min-height: 300px; height: 100%;"
      ></div>
      }
    </div>
  `,
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    },
  ],
  styles: `
    .bg-white-field {
      --mdc-filled-text-field-container-color: var(--abb-color-white);
      --mat-form-field-state-layer-color: transparent;
    }
  `,
  host: {
    class: 'flex flex-col flex-1',
  },
})
export class AbbAddressFieldComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @ViewChild('mapContainer', { static: false }) mapContainer!: ElementRef;
  prefix = inject(TRANSLATION_PREFIX);
  whiteField = input<boolean>(false);
  getCountryDetails = input<boolean>(true);
  mode = input<'new' | 'edit'>('new');
  showMap = input<boolean>(false);
  validatedAddress = signal(false);
  isAddressValid = output<boolean>();
  map: maplibregl.Map | undefined;
  private markers: maplibregl.Marker[] = [];
  suggestions = signal<any[]>([]);
  isFocused = signal(false);

  private readonly subscriptions: Subscription[] = [];
  private client!: LocationClient;
  constructor(
    public parent: FormGroupDirective,
    private countrydetailservice: CountryDetailsService
  ) {
    this.initializeAWSClient(async () => {
      if (this.mode() === 'edit') {
        const fullAddress = this.parent.form.controls['fullAddress'].value;
        this.patchPlaceDetails(fullAddress);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.map) {
      this.map.remove();
    }
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  async initializeAWSClient(callback?: () => void) {
    const authHelper = await withAPIKey(environment.maps.apiKey);
    this.client = new LocationClient({
      region: environment.maps.region,
      ...authHelper.getLocationClientConfig(),
    });
    callback?.();
  }

  ngOnInit(): void {
    this.setupAddressSearch();
  }

  ngAfterViewInit(): void {
    if (this.showMap()) {
      this.loadMap();
    }
  }

  clearSearch() {
    this.parent.form.controls['fullAddress'].reset();
    this.parent.form.controls['country'].reset();
    this.parent.form.controls['city'].reset();
    this.parent.form.controls['postalCode'].reset();
    this.parent.form.controls['address'].reset();
    if (this.parent.form.get('coordinates')) {
      this.parent.form.controls['coordinates'].reset();
    }
    this.validatedAddress.set(false);
    this.isAddressValid.emit(this.validatedAddress());
  }

  setupAddressSearch() {
    const sub = this.parent.valueChanges
      ?.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((data) => {
          return this.searchPlaces(data?.fullAddress);
        })
      )
      .subscribe((results) => {
        this.suggestions.set(results.Results || []);
      });
    if (sub) {
      this.subscriptions.push(sub);
    }
  }

  async searchPlaces(query: string | null) {
    if (!query) {
      return { Results: [] };
    }

    const input = {
      IndexName: environment.maps.indexName,
      Text: query,
    };

    try {
      const command = new SearchPlaceIndexForSuggestionsCommand(input);
      const response = await this.client.send(command);
      return response;
    } catch (error) {
      console.error('Error searching places:', error);
      return { Results: [] };
    }
  }

  async patchPlaceDetails(fullAddress: string) {
    const detailedPlace = await this.getPlaceDetails(fullAddress);
    if (detailedPlace) {
      this.parent.control?.patchValue({
        country: detailedPlace?.Place?.Country ?? '',
        city: detailedPlace?.Place?.Municipality ?? '',
        postalCode: detailedPlace?.Place?.PostalCode ?? '',
        address: detailedPlace?.Place?.Street
          ? detailedPlace?.Place?.Street +
            (detailedPlace?.Place?.AddressNumber
              ? `, ${detailedPlace?.Place?.AddressNumber}`
              : '')
          : '',
      });

      if (this.getCountryDetails()) {
        this.countrydetailservice.getCountryDetails(
          detailedPlace?.Place?.Country ?? ''
        );
      }

      if (this.showMap()) {
        if (this.markers.length > 0) {
          this.markers.forEach((marker) => marker.remove());
          this.markers = [];
        }

        if (detailedPlace.Place!.Geometry!.Point) {
          const coordinates = detailedPlace.Place!.Geometry!.Point as [
            number,
            number
          ];

          this.map?.setCenter(coordinates);
          this.map?.setZoom(15);

          const marker = new maplibregl.Marker().setLngLat(coordinates);
          if (this.map) {
            marker.addTo(this.map);
          }
          if (this.parent.form.get('coordinates')) {
            this.parent.control?.patchValue({
              coordinates: { lat: coordinates[1], lng: coordinates[0] },
            });
          }
          this.markers.push(marker);
        }
      }
      this.validatedAddress.set(true);
    } else {
      this.validatedAddress.set(false);
    }

    this.isAddressValid.emit(this.validatedAddress());
  }

  async selectPlace(place: any) {
    const fullAddress = place.Text;
    this.parent.control?.patchValue({
      fullAddress,
    });
    await this.patchPlaceDetails(fullAddress);
    this.suggestions.set([]);
    this.isFocused.set(false);
  }

  async getPlaceDetails(
    placeName: string
  ): Promise<SearchForTextResult | null> {
    const input = {
      IndexName: environment.maps.indexName,
      Text: placeName,
      MaxResults: 1,
    };

    try {
      const command = new SearchPlaceIndexForTextCommand(input);
      const response: SearchPlaceIndexForTextCommandOutput =
        await this.client.send(command);
      return response.Results && response.Results.length > 0
        ? response.Results[0]
        : null;
    } catch (error) {
      console.error('Error getting place details:', error);
      return null;
    }
  }

  private centerMapOnUserLocation(): Promise<any> {
    return new Promise((resolve, reject) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const userCoords = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
            // this.map.setCenter(userCoords);
            resolve(userCoords);
          },
          (error) => {
            console.error('Error getting location', error);
            resolve({ lat: 45.4642, lng: 9.19 });
            reject(error);
          }
        );
      } else {
        const errorMsg = 'Geolocation is not supported by this browser.';
        console.error(errorMsg);
        reject(new Error(errorMsg)); // Rigetta se la geolocalizzazione non è supportata
      }
    });
  }

  private loadMap(): void {
    this.centerMapOnUserLocation().then((coords) => {
      if (!this.map) {
        this.map = new maplibregl.Map({
          container: this.mapContainer?.nativeElement,
          style: `https://maps.geo.${environment.maps.region}.amazonaws.com/maps/v0/maps/${environment.maps.mapName}/style-descriptor?key=${environment.maps.apiKey}`,
          center: [coords.lng, coords.lat],
          zoom: 9,
        });

        this.map?.on('load', () => {
          console.log('Map loaded');
          this.map?.addControl(
            new maplibregl.NavigationControl(),
            'bottom-right'
          );

          this.centerMapOnUserLocation();
          if (this.mode() == 'edit') {
            const coordinatesCtrl = this.parent.form.get('coordinates');
            if (coordinatesCtrl) {
              const coordinates = coordinatesCtrl.value;
              const marker = new maplibregl.Marker({
                element: this.createCircleMarker(),
              }).setLngLat([coordinates.lng!, coordinates.lat!]);
              if (this.map) {
                marker.addTo(this.map);
              }

              this.map?.flyTo({
                center: [coordinates.lng!, coordinates.lat!],
                duration: 3000,
                zoom: 13,
              });
              this.markers.push(marker);
            }
          }
        });
      }
    });
  }

  private createCircleMarker(): HTMLElement {
    const marker = document.createElement('div');
    marker.className = 'custom-marker';
    marker.style.width = '18px';
    marker.style.height = '18px';
    marker.style.borderRadius = '50%';
    marker.style.backgroundColor = 'rgb(255 0 15)';
    marker.style.border = '2px solid white';
    marker.style.boxShadow = '2px 2px 10px rgba(0,0,0,0.3)';
    return marker;
  }

  onBlur(): void {
    const addressFieldsCtrl = [
      this.parent.form.controls['fullAddress'],
      this.parent.form.controls['country'],
      this.parent.form.controls['city'],
      this.parent.form.controls['postalCode'],
      this.parent.form.controls['address'],
    ];
    if (this.parent.form.get('coordinates')) {
      addressFieldsCtrl.push(this.parent.form.controls['coordinates']);
    }
    if (!this.validatedAddress()) {
      addressFieldsCtrl.forEach((ctrl) => {
        if (ctrl.value) {
          ctrl.setErrors({ ...ctrl.errors, invalid: true });
        }
      });
    }

    this.isAddressValid.emit(this.validatedAddress());
  }
}
